import type {
  CurrentAdminRbacPermissionsResponse,
  RbacRole,
  RbacRolesWithoutHolderResponse,
} from '@gozoki/api-types'
import { queryOptions, skipToken } from '@tanstack/react-query'
import { gozokiApiWithAuth } from '../gozokiApi'

// ---------------------------------------------------------------------------
// Lookup « admin by email » — Task 6 backend, Task 7 front consumer
// ---------------------------------------------------------------------------

export interface AdminByEmailAccount {
  adminId: number
  firstName: string
  lastName: string
  /** True si le compte porte déjà au moins un rôle RBAC. */
  hasRole: boolean
  /** True si une fiche collaborateur est liée à ce compte. */
  hasFiche: boolean
}

export interface AdminByEmailResponse {
  account: AdminByEmailAccount | null
}

/** Looks up an existing BO account by email. Returns null when no account exists. */
export const fetchAdminByEmail = async (email: string): Promise<AdminByEmailResponse> => {
  const response = await gozokiApiWithAuth.get<AdminByEmailResponse>('/rbac/admins/by-email', {
    params: { email },
  })
  return response.data
}

/** Simple email-shape guard: non-empty and contains @. */
const isEmailLike = (email: string) => email.trim().length > 0 && email.includes('@')

export const adminByEmailQueryOptions = (email: string) =>
  queryOptions({
    queryKey: ['rbac', 'admin-by-email', email.trim().toLowerCase()] as const,
    queryFn: isEmailLike(email) ? () => fetchAdminByEmail(email.trim()) : skipToken,
    staleTime: 30_000,
  })

export const fetchRbacRoles = async () => {
  const response = await gozokiApiWithAuth.get<RbacRole[]>('/rbac/roles')
  return response.data
}

export const fetchRbacRole = async (roleId: number) => {
  const response = await gozokiApiWithAuth.get<RbacRole>(`/rbac/roles/${roleId}`)
  return response.data
}

export const fetchCurrentAdminRbacPermissions = async () => {
  const response =
    await gozokiApiWithAuth.get<CurrentAdminRbacPermissionsResponse>('/rbac/me/permissions')
  return response.data
}

export const fetchRbacRolesWithoutHolder = async () => {
  const response = await gozokiApiWithAuth.get<RbacRolesWithoutHolderResponse>(
    '/rbac/roles-without-holder',
  )
  return response.data
}
